
import { Visibility } from "@mui/icons-material";
import { IconButton, InputAdornment, InputLabel } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import bgImage from "assets/images/logos/logo_icon.png";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { errorAlert, successAlert } from "utils/Functions";
import { baseURL, get, getEncryptPublicAuth, postNoResponse } from "utils/WebService";

export default function SignInIllustration() {

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  // Recovering settings
  const [isRecovering, setIsRecovering] = useState(false);

  // 2FA states
  const [is2faMode, setIs2faMode] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);
  const [twoFactorMethod, setTwoFactorMethod] = useState("APP");
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [loginError, setLoginError] = useState("");

  const [showPasswordIndex, setShowPasswordIndex] = useState(null);

  const togglePasswordVisibility = () => {
    setShowPasswordIndex(!showPasswordIndex);
  };

  const onSubmit = event => {
    event.preventDefault();
    const recoveryError = () => errorAlert("Erro ao enviar o e-mail, tente novamente mais tarde.", () => {
      setLoading(false);
    })
    setLoading(true);
    if (isRecovering) {
      fetch(`${baseURL}/users/public/reset-password?email=${username}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': getEncryptPublicAuth(),
        },
      }).then(res => {
        if (res == null || !res.ok) {
          recoveryError();
        }
        successAlert("Se o e-mail estiver registrado, será enviado um e-mail de recuperação.", () => {
          setLoading(false);
          setIsRecovering(false);
        })
      }).catch(() => recoveryError());
    } else {
      login();
    }

  }

  const finishLogin = (token) => {
    sessionStorage.setItem('user', token);
    sessionStorage.removeItem('selectedPages');
    sessionStorage.removeItem('pendingDevedorId');
    localStorage.removeItem('pendingDevedorId');
    localStorage.removeItem('temp_share_selected_pages');
    get('/users/by-token')
      .then(({ id }) => {
        if (id != null) {
          get('/users/by-id/'.concat(id))
            .then(({ ativo }) => {
              if (ativo) {
                setLoading(false);
                navigate("/system");
              } else {
                blocked();
              }
            });
        }
      });
  };

  async function login() {
    const auth = btoa(`${username}:${password}`);
    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${auth}`,
      },
    };

    const resp = await fetch(baseURL + '/users/login', requestOptions)
      .then(res => res)
      .catch(() => null);

    const user = await handleResponse(resp);
    if (user) {
      if (user.status === "REQUIRE_2FA") {
        setTwoFactorData(user);
        setIs2faMode(true);
        if (user.methods.includes("APP")) {
          setTwoFactorMethod("APP");
          setLoading(false);
        } else {
          setTwoFactorMethod("EMAIL");
          sendEmailCode(user.tempToken);
        }
        return;
      }
      finishLogin(user.token);
    } else {
      sessionStorage.removeItem('user');
      sessionStorage.removeItem('selectedPages');
    }
  }

  const submit2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const resp = await fetch(baseURL + '/users/public/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tempToken: twoFactorData.tempToken,
          code: twoFactorCode,
          method: twoFactorMethod
        })
      });
      if (!resp.ok) {
        errorAlert("Código inválido ou expirado.", () => setLoading(false));
        return;
      }
      const data = await resp.json();
      finishLogin(data.token);
    } catch (err) {
      errorAlert("Erro ao validar código.", () => setLoading(false));
    }
  };

  const sendEmailCode = async (token = twoFactorData?.tempToken) => {
    setLoading(true);
    try {
      const resp = await fetch(baseURL + '/users/public/2fa/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tempToken: token })
      });
      if (resp.ok) {
        successAlert("Código enviado para o seu e-mail!", () => {
          setTwoFactorMethod("EMAIL");
          setLoading(false);
        });
      } else {
        errorAlert("Erro ao enviar e-mail.", () => setLoading(false));
      }
    } catch (err) {
      errorAlert("Erro ao enviar e-mail.", () => setLoading(false));
    }
  };

  function recoverPassword() {
    setLoading(true);
    postNoResponse(`/users/public/reset-password?email=${username}`)
      .then(() => successAlert("Se o e-mail estiver registrado, será enviado um e-mail de recuperação.", () => {
        setLoading(false);
        setIsRecovering(false);
      }))
      .catch(() => errorAlert("Erro ao enviar o e-mail, tente novamente mais tarde.", () => {
        setLoading(false);
      }))

  }

  function logout() {
    setLoading(false);
    setLoginError("Credenciais inválidas.");
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('selectedPages');
    sessionStorage.removeItem('tema');
  }

  function blocked() {
    setLoading(false);
    setLoginError("Credenciais inválidas.");
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('selectedPages');
    sessionStorage.removeItem('tema');
  }

  const hideAlert = () => null

  const handleResponse = response => {
    if (response == null || !response.ok) {
      logout();
      return false;
    }
    return response.json();
  }

  return (
    <IllustrationLayout
      title={is2faMode ? "Autenticação em 2 Fatores" : isRecovering ? "Recuperação de Acesso" : ""}
      description={is2faMode ? "Verificação de segurança" : (isRecovering ? "Digite seu e-mail para recuperar o acesso" : "")}
      illustration={bgImage}
    >
      {!is2faMode ? (
        <MDBox component="form" onSubmit={onSubmit}>
          <MDBox mt={-2}>
            <InputLabel sx={{ px: 1, pb: 0.5 }}>{isRecovering ? "E-mail" : "Login"}</InputLabel>
            <MDInput
              type="text"
              fullWidth
              placeholder={isRecovering ? "Informe seu e-mail cadastrado" : "Informe seu nome ou e-mail"}
              value={username}
              onChange={({ target }) => { setUsername(target.value); setLoginError(""); }} />
          </MDBox>

          {!isRecovering && <MDBox mt={2}>
            <InputLabel sx={{ px: 1, pb: 0.5 }}>Password</InputLabel>
            <MDInput
              type={showPasswordIndex ? "text" : "password"}
              fullWidth
              value={password}
              onChange={({ target }) => { setPassword(target.value); setLoginError(""); }}
              placeholder="Informe a senha"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    <IconButton
                      onClick={() => togglePasswordVisibility()}
                      edge="end"
                    >
                      <Visibility sx={{ color: '#7b809a' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </MDBox>}

          {!isRecovering && loginError && (
            <MDBox mt={1} mb={-1}>
              <MDTypography variant="caption" color="error" fontWeight="medium">
                {loginError}
              </MDTypography>
            </MDBox>
          )}

          <MDBox mt={2} mb={1} display="flex">
            <MDButton
              variant="gradient"
              color="info"
              size="large"
              type="submit"
              fullWidth
              disabled={loading || !username || (!isRecovering && !password)}
              endIcon={loading ? <CircularProgress color="inherit" size={20} /> : null}>
              {loading ? "" : isRecovering ? "Enviar e-mail de recuperação" : "entrar"}
            </MDButton>
          </MDBox>
          <MDBox textAlign="center">
            <MDTypography
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
              onClick={() => setIsRecovering(!isRecovering)}
              sx={{ cursor: "pointer" }} >
              {isRecovering ? "Voltar para o Login" : "Esqueci a senha"}
            </MDTypography>
          </MDBox>
        </MDBox>
      ) : (
        <MDBox component="form" onSubmit={submit2FA}>
          <MDBox mt={-2} mb={2}>
            <MDTypography variant="body2" color="text">
              {twoFactorMethod === "APP" 
                ? "Abra seu aplicativo autenticador (ex: Google Authenticator) e insira o código numérico." 
                : "Insira o código de 6 dígitos enviado para o seu e-mail."}
            </MDTypography>
          </MDBox>
          <MDBox mt={2}>
            <InputLabel sx={{ px: 1, pb: 0.5 }}>Código de Acesso</InputLabel>
            <MDInput
              type="text"
              fullWidth
              placeholder="000000"
              value={twoFactorCode}
              onChange={({ target }) => setTwoFactorCode(target.value)} />
          </MDBox>

          <MDBox mt={2} mb={1} display="flex">
            <MDButton
              variant="gradient"
              color="info"
              size="large"
              type="submit"
              fullWidth
              disabled={loading || !twoFactorCode}
              endIcon={loading ? <CircularProgress color="inherit" size={20} /> : null}>
              {loading ? "" : "Verificar Código"}
            </MDButton>
          </MDBox>

          {twoFactorData && twoFactorData.methods && twoFactorData.methods.includes("EMAIL") && twoFactorMethod === "APP" && (
             <MDBox textAlign="center" mt={2}>
               <MDTypography
                 variant="button"
                 color="info"
                 fontWeight="medium"
                 textGradient
                 onClick={() => sendEmailCode()}
                 sx={{ cursor: "pointer" }} >
                 Não tem o app? Enviar código por e-mail
               </MDTypography>
             </MDBox>
          )}

          <MDBox textAlign="center" mt={1}>
             <MDTypography
               variant="button"
               color="secondary"
               fontWeight="medium"
               onClick={() => { setIs2faMode(false); setTwoFactorCode(""); setPassword(""); }}
               sx={{ cursor: "pointer" }} >
               Voltar para o Login
             </MDTypography>
          </MDBox>
        </MDBox>
      )}
    </IllustrationLayout >
  );
}

